import React from 'react';

import { PALETTE } from './constants';
import { DashboardLoading, fmt } from './dashboard/dashboardFormatting';
import FocusTable from './dashboard/FocusTable';
import HBars from './dashboard/HBars';
import RadioDashboardSkeleton from './dashboard/RadioDashboardSkeleton';
import RadioDonut from './dashboard/RadioDonut';
import { useGeneralRisk } from './hooks/useGeneralRisk';

// IB-677 rework — Opportunities dashboard. Lead tile = weighted + unweighted value;
// profile + key metric use the AFC split. PR review (#1060): the wireframe's
// "banked" tile has no defined signal in the schema and Dave asked to drop it
// entirely rather than ship it as "Not tracked" — the status-highlight position
// now shows the not-in-AFC count instead.
export default function GeneralRiskDashboardOpportunities() {
    const { dashboardData, loading } = useGeneralRisk();

    if (loading.dashboard) {
        return <DashboardLoading />;
    }

    const opportunities = dashboardData?.opportunities || {};
    const overview = dashboardData?.overview || {};
    const inAfc = opportunities.in_afc_count ?? 0;
    const notInAfc = opportunities.not_in_afc_count ?? 0;
    const open = inAfc + notInAfc;

    const tiles = [
        {
            lead: true,
            label: $t('Weighted value'),
            value: fmt(opportunities.weighted_value),
            sub: `${$t('Unweighted value')} ${fmt(opportunities.unweighted_value)}`,
        },
        { label: $t('Open opportunities'), value: open, sub: `${inAfc} ${$t('in AFC')} · ${notInAfc} ${$t('not in AFC')}` },
        { label: $t('Not in AFC'), value: notInAfc, sub: `${$t('of')} ${open}` },
        { tone: 'warn', label: $t('Overdue actions'), value: '—' },
        { label: $t('Linked to budget lines'), value: opportunities.linked_to_budget_lines_count ?? 0, sub: `${$t('of')} ${open}` },
    ];

    const profile = {
        title: $t('AFC profile'),
        content: (
            <RadioDonut
                total={`${open} ${$t('open')}`}
                segments={[
                    { value: inAfc, colour: PALETTE.blue, label: `${$t('In AFC')} · ${inAfc}` },
                    { value: notInAfc, colour: PALETTE.charcoal25, label: `${$t('Not in AFC')} · ${notInAfc}` },
                ]}
            />
        ),
    };

    const keyMetric = {
        title: $t('Weighted value by AFC'),
        content: (
            <HBars
                labelWidth={90}
                rows={[
                    { label: $t('In AFC'), segments: [{ value: opportunities.in_afc ?? 0, colourClass: 'tw-bg-green' }] },
                    { label: $t('Not in AFC'), segments: [{ value: opportunities.not_in_afc ?? 0, colourClass: 'tw-bg-green-25' }] },
                ]}
                emptyLabel={$t('No opportunity value')}
            />
        ),
    };

    const highest = opportunities.highest_value || [];
    const focus = (
        <FocusTable
            title={$t('Highest value opportunities')}
            columns={[$t('Opportunity'), $t('Amount'), $t('Weighted value')]}
            rows={highest.map((opportunity) => [opportunity.name, fmt(opportunity.amount), fmt(opportunity.weighted_value)])}
            empty={$t('No opportunities')}
        />
    );

    return (
        <RadioDashboardSkeleton
            tiles={tiles}
            profile={profile}
            movement={{ data: overview.movement, category: 'opportunities' }}
            keyMetric={keyMetric}
            focus={focus}
        />
    );
}
